import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { adminApiHeader, finalRouteApi } from '../components/CommonApis/CommonApiUrl';
import MessageHeading from '../components/CommonComponents/MessageHeading';
import AdminCustomInput from '../components/CommonComponents/AdminCustomInput';

// Fallback mapping so canonical singular identifiers can read legacy plural assignments pre-migration
const CANONICAL_TO_ALIASES = {
  'audio.create': ['audios.create'],
  'audio.edit': ['audios.edit'],
  'livestream.create': ['livestreams.create'],
  'livestream.edit': ['livestreams.edit'],
  'faq_category.create': ['faq_categories.create'],
  'faq_category.edit': ['faq_categories.edit'],
  'advertisement.create': ['advertisements.create'],
  'advertisement.edit': ['advertisements.edit'],
  'language.create': ['languages.create'],
  'language.edit': ['languages.edit'],
  'landing_page.create': ['landing_pages.create'],
  'landing_page.edit': ['landing_pages.edit'],
  'site_partner.create': ['partners.create'],
  'site_partner.edit': ['partners.edit'],
  // historically used in forms (rare) but not in editor; safe fallback
  'ads_category.create': ['ads_categories.create'],
  'ads_category.edit': ['ads_categories.edit'],
};

export function useCustomFields(pageIdentifier, entityType, entityId) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fields, setFields] = useState([]);
  const [rules, setRules] = useState([]);
  const [values, setValues] = useState({});

  useEffect(() => {
    let mounted = true;
    async function init() {
      setLoading(true); setError('');
      try {
        async function fetchFor(ident) {
          return axios.get(`${finalRouteApi}/custom-fields-for-form`, {
            params: { pageIdentifier: ident }, headers: adminApiHeader
          });
        }

        // 1) Try canonical identifier first
        let resp = await fetchFor(pageIdentifier);
        let fieldsData = resp.data?.fields || [];
        let rulesData = resp.data?.rules || [];

        // 2) If no fields, try legacy aliases (pre-migration safety)
        if ((!fieldsData || fieldsData.length === 0) && CANONICAL_TO_ALIASES[pageIdentifier]?.length) {
          for (const alias of CANONICAL_TO_ALIASES[pageIdentifier]) {
            try {
              const aliasResp = await fetchFor(alias);
              const aFields = aliasResp.data?.fields || [];
              if (aFields.length > 0) {
                fieldsData = aFields;
                rulesData = aliasResp.data?.rules || [];
                break;
              }
            } catch (_) { /* ignore alias errors */ }
          }
        }

        if (!mounted) return;
        setFields(fieldsData || []);
        setRules(rulesData || []);

        if (entityType && entityId) {
          try {
            const v = await axios.get(`${finalRouteApi}/custom-field-values`, {
              params: { entity_type: entityType, entity_id: entityId }, headers: adminApiHeader
            });
            if (mounted) setValues(v.data?.values || {});
          } catch (e) { /* ignore value fetch errors */ }
        }
      } catch (e) {
        if (mounted) setError(e?.response?.data?.message || e.message);
      } finally { if (mounted) setLoading(false); }
    }
    if (pageIdentifier) init();
    return () => { mounted = false; };
  }, [pageIdentifier, entityType, entityId]);

  return { loading, error, fields, rules, values, setValues };
}

export default function FieldRenderer({ pageIdentifier, entityType, entityId, onChange }) {
  const { loading, error, fields, rules, values, setValues } = useCustomFields(pageIdentifier, entityType, entityId);

  const visibleFields = useMemo(() => {
    // simple client-side conditional rules evaluation
    function evaluate(rule) {
      const val = values[rule.source_field_key] ?? '';
      switch (rule.operator) {
        case 'equals': return String(val) === String(rule.value);
        case 'notEquals': return String(val) !== String(rule.value);
        case 'in': return (Array.isArray(rule.value) ? rule.value : String(rule.value).split(',')).includes(String(val));
        case 'notIn': return !(Array.isArray(rule.value) ? rule.value : String(rule.value).split(',')).includes(String(val));
        case 'greaterThan': return Number(val) > Number(rule.value);
        case 'lessThan': return Number(val) < Number(rule.value);
        default: return true;
      }
    }

    // Map target field id to effects
    const effects = {};
    for (const r of (rules || [])) {
      if (!effects[r.target_field_id]) effects[r.target_field_id] = [];
      effects[r.target_field_id].push(r);
    }

    return (fields || []).map(f => {
      const applied = { ...f };
      const rls = effects[f.id] || [];
      for (const r of rls) {
        const match = evaluate(r);
        if (r.effect === 'hide' && match) applied.__hidden = true;
        if (r.effect === 'show' && !match) applied.__hidden = true;
        if (r.effect === 'require' && match) applied.required = true;
      }
      return applied;
    }).filter(f => !f.__hidden);
  }, [fields, rules, values]);

  const setVal = (key, v) => {
    const next = { ...values, [key]: v };
    setValues(next);
    onChange && onChange(next);
  };

  // if (loading) return <div className="mb-2">Loading custom fields…</div>;
  // if (error) return <div className="text-danger">{error}</div>;
  if (!visibleFields.length) return null;

  const input = (f) => {
    const v = values[f.key] ?? '';
    switch (f.type) {
      case 'text':
        return <AdminCustomInput value={v} onChange={e => setVal(f.key, e.target.value)} />
        // return <input className="form-control" value={v} onChange={e => setVal(f.key, e.target.value)} />;
      case 'textarea':
        return <textarea className="form-control" value={v} onChange={e => setVal(f.key, e.target.value)} />;
      case 'number':
        return <input type="number" className="form-control" value={v} onChange={e => setVal(f.key, e.target.value)} />;
      case 'date':
        return <input type="date" className="form-control" value={v} onChange={e => setVal(f.key, e.target.value)} />;
      case 'select':
        return (
          <select className="form-control" value={v} onChange={e => setVal(f.key, e.target.value)}>
            <option value=''>-- select --</option>
            {(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        );
      case 'multiselect':
        return (
          <select multiple className="form-control" value={Array.isArray(v) ? v : (v ? String(v).split(',') : [])} onChange={e => setVal(f.key, Array.from(e.target.selectedOptions).map(o => o.value))}>
            {(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        );
      case 'checkbox':
        return (
          <div>
            {(f.options || []).map(o => {
              const cur = Array.isArray(v) ? v : (v ? String(v).split(',') : []);
              const checked = cur.includes(String(o.value));
              return (
                <label key={o.value} style={{ marginRight: 12 }}>
                  <input type="checkbox" checked={checked} onChange={e => {
                    const next = new Set(cur);
                    if (e.target.checked) next.add(String(o.value)); else next.delete(String(o.value));
                    setVal(f.key, Array.from(next));
                  }} /> {o.label}
                </label>
              );
            })}
          </div>
        );
      case 'radio':
        return (
          <div>
            {(f.options || []).map(o => (
              <label key={o.value} style={{ marginRight: 12 }}>
                <input type="radio" name={f.key} value={o.value} checked={String(v) === String(o.value)} onChange={e => setVal(f.key, e.target.value)} /> {o.label}
              </label>
            ))}
          </div>
        );
      case 'file':
        return <input type="file" className="form-control" onChange={e => setVal(f.key, e.target.files?.[0] || null)} />;
      default:
        return <input className="form-control" value={v} onChange={e => setVal(f.key, e.target.value)} />;
    }
  };

  return (
    <div className="custom-field-renderer">
      {visibleFields?.length > 0 && (
        <>
          <MessageHeading text="Additional Fields" />
        </>
      )}
    </div>
  );
}

